import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MosaicRequestDialog } from "@/components/dealer/MosaicRequestDialog";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePickerField } from "@/components/DatePickerField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Loader2, ChevronRight } from "lucide-react";
import { CustomStudioLines, CUSTOM, projectTotal, lineAllocations, lineQty } from "@/components/custom/CustomStudioLines";
import { StatusChip } from "@/components/custom/stageColors";

const PRODUCT_TYPES = ["Field tile", "Mosaic", "Trim / edge", "Panel / mural", "Other"];

// RC51-QA-003/006: request level = design name, product type, project, delivery date, notes; everything physical lives on the lines.
export default function CustomStudio() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [colors, setColors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ design_name: "", product_type: "Field tile", desired_delivery_date: "", description: "", project_id: "", new_project_name: "" });
  const [lines, setLines] = useState([]);

  const load = () => api.get("/dealer/custom-studio").then((r) => setRows(r.data)).catch(() => setRows([]));
  const [params] = useSearchParams();
  const mosaicId = params.get("design_id");
  const [mosaicOpen, setMosaicOpen] = useState(!!mosaicId);
  useEffect(() => {
    load();
    api.get("/catalog").then((r) => {
      const seen = new Map();
      (r.data || []).forEach((v) => { if (!seen.has(v.color_code)) seen.set(v.color_code, { name: v.color_name || v.color_code, us_name: v.us_color_name || "", moroccan_name: v.moroccan_color_name || v.color_moroccan_name || "" }); });
      setColors([...seen.entries()].map(([code, m]) => ({ code, ...m })).sort((a, b) => a.code.localeCompare(b.code)));
    }).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const total = projectTotal(lines);
  const blocked = lines.some((l) => l.moq_blocked);
  const canSubmit = lines.length > 0 && form.project_id && (form.project_id !== "__new__" || form.new_project_name.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/dealer/custom-studio", {
        design_name: form.design_name, product_type: form.product_type,
        desired_delivery_date: form.desired_delivery_date || null, description: form.description || null,
        lines: lines.map((l) => ({
          format_in: l.format_mode === "CUSTOM" ? null : l.format_in, format_mode: l.format_mode || "STANDARD",
          custom_shape_desc: l.format_mode === "CUSTOM" ? l.custom_shape_desc || null : null,
          dimensions: l.format_mode === "CUSTOM" ? l.dimensions || null : null,
          composition: "SOLID", composition_description: null, allocations: lineAllocations(l),
          colors: l.colors.map((c) => (c.code === CUSTOM
            ? { code: null, brand: c.custom.brand || null, manufacturer_code: c.custom.code || null, pantone: c.custom.pantone || null, ref: c.custom.ref || null }
            : { code: c.code })),
          quantity: lineQty(l), quantity_unit: l.format_mode === "CUSTOM" ? "SQFT" : l.quantity_unit, quantity_basis: l.quantity_basis, application: l.application,
          pattern_description: l.pattern_description || null, notes: l.notes || null,
          attachment_tokens: (l.files || []).map((f) => f.token),
        })),
        quantity_sqft: null,
        project_id: form.project_id && form.project_id !== "__new__" ? form.project_id : null,
        new_project_name: form.project_id === "__new__" ? form.new_project_name.trim() || null : null,
      });
      toast.success(`Quote request ${data.request_number} sent — Zelligio will review it`);
      setOpen(false);
      navigate(`/dealer/custom-studio/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="custom-studio-page">
      <MosaicRequestDialog designId={mosaicId} sourceSku={params.get("sku")} open={mosaicOpen} onOpenChange={setMosaicOpen} onCreated={load} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-terracotta">Custom Studio</p>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-charcoal">Send us your idea</h1>
          <p className="mt-2 max-w-2xl text-sm text-warmgrey">
            Can't find what you need in the catalog? Custom colors, shapes, formats and designs —
            handmade for your project. Zelligio handles feasibility, sampling and production.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-custom-request-button"
          className="h-11 bg-terracotta text-white hover:bg-terracotta-hover">
          <Sparkles className="mr-2 h-4 w-4" /> New custom request
        </Button>
      </div>

      <div className="mt-8 space-y-3">
        {rows === null && <Loader2 className="h-5 w-5 animate-spin text-terracotta" />}
        {rows?.length === 0 && (
          <div className="rounded-md border border-border bg-white p-8 text-center" data-testid="custom-studio-empty">
            <Sparkles className="mx-auto h-6 w-6 text-terracotta" />
            <p className="mt-3 text-sm font-medium text-charcoal">No custom requests yet</p>
            <p className="mt-1 text-sm text-warmgrey">Send us your idea — save the deal when the catalog doesn't fit.</p>
          </div>
        )}
        {rows?.map((r) => (
          <Link key={r.id} to={`/dealer/custom-studio/${r.id}`} data-testid={`custom-request-row-${r.id}`}
            className="flex items-center justify-between gap-3 rounded-md border border-border bg-white p-4 transition-colors hover:bg-sand">
            <div className="min-w-0">
              <p className="text-sm font-medium text-charcoal">{r.request_number} — {r.design_name || r.product_type}</p>
              <p className="text-xs text-muted-foreground">
                {r.project_name ? `${r.project_name} · ` : ""}{r.lines?.length ? `${r.lines.length} line${r.lines.length > 1 ? "s" : ""} · ` : ""}{r.color_mode === "CUSTOM" ? "Custom color" : "Catalog colors"} · {r.format_mode === "CUSTOM" ? "Custom shape" : r.format_in || (r.lines?.[0]?.format_mode === "MOSAIC" ? `${r.lines[0].family} mosaic` : "—")} · {r.quantity_sqft} {({ SQFT: "sq ft", LINEAR_FT: "LF", PIECES: "pcs" })[r.quantity_unit] || "sq ft"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {r.reorderable && <span className="rounded-full bg-sand px-2.5 py-1 text-[10px] font-medium text-charcoal" data-testid={`custom-reorderable-${r.id}`}>Approved · reorderable</span>}
              {r.reorder_of && <span className="rounded-full bg-sand px-2.5 py-1 text-[10px] font-medium text-warmgrey" data-testid={`custom-reorder-tag-${r.id}`}>Reorder of {r.reorder_of.request_number}</span>}
              <StatusChip status={r.status} label={r.dealer_status} testid={`custom-status-${r.id}`} />
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </Link>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden border-border bg-white sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Request a custom quote</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              Describe the request once, then add one product line per color, format and location. Files attach to the line they belong to.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="mt-2 space-y-5" data-testid="custom-request-form">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Custom design name *</Label>
              <Input required value={form.design_name} onChange={(e) => setForm((f) => ({ ...f, design_name: e.target.value }))}
                placeholder="e.g. Pool Perimeter Border" data-testid="cr-design-name"
                className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Product type *</Label>
                <Select value={form.product_type} onValueChange={(v) => setForm((f) => ({ ...f, product_type: v }))}>
                  <SelectTrigger data-testid="cr-product-type" className="h-11 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{PRODUCT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Project *</Label>
                <Select value={form.project_id} onValueChange={(v) => setForm((f) => ({ ...f, project_id: v }))}>
                  <SelectTrigger data-testid="cr-project" className="h-11 bg-white"><SelectValue placeholder="Select or create a project" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="__new__">Create new project…</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.project_id === "__new__" && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">New project name *</Label>
                <Input required value={form.new_project_name} onChange={(e) => setForm((f) => ({ ...f, new_project_name: e.target.value }))}
                  placeholder="e.g. Smith Residence — Pool Renovation" data-testid="cr-new-project-name"
                  className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Desired delivery date</Label>
                <DatePickerField value={form.desired_delivery_date} min="today"
                  onChange={(v) => setForm((f) => ({ ...f, desired_delivery_date: v }))} testId="cr-delivery-date" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Overall quantity (from lines)</Label>
                <div className="flex h-11 items-center rounded-md border border-border bg-sand/40 px-3 text-sm text-charcoal" data-testid="cr-quantity">
                  {lines.length ? <>{total.sqft.toFixed(2)} sq ft · {total.boxes.toFixed(1)} boxes · {lines.length} line{lines.length > 1 ? "s" : ""}</> : <span className="text-warmgrey">Add product lines below</span>}
                </div>
              </div>
            </div>

            <CustomStudioLines lines={lines} setLines={setLines} colors={colors} />

            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Overall project notes</Label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Anything that applies to the whole request — client context, constraints, timing"
                data-testid="cr-description" className="min-h-[80px] bg-white focus-visible:ring-terracotta" />
            </div>

            <Button type="submit" disabled={submitting || !canSubmit} data-testid="cr-submit"
              className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request a Quote"}
            </Button>
            {lines.length === 0 && <p className="text-center text-xs text-warmgrey" data-testid="cr-need-lines">Add at least one product line to request a quote.</p>}
            {blocked && (
              <p className="text-center text-xs text-status-reserved" data-testid="cr-below-min">
                A product line is below the 300 sq ft guideline — you can submit; the request will be flagged for Zelligio admin review before any proposal.
              </p>
            )}
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
